import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { CustomerPicker } from '../ui/CustomerPicker';
import {
  LineItemBuilder,
  emptyLine,
  parseLines,
  type LineDraft,
} from '../money/LineItemBuilder';
import { Field, field } from './Field';
import { TemplatePicker } from './TemplatePicker';
import { TaxRateField, taxBps } from '../money/TaxRateField';
import {
  formatCommercialJobNumber,
  parseQuoteLines,
  type QuoteCreateInput,
} from '@servwell/shared';
import { QuoteLineGrid, QuoteTotalsPreview } from '../money/QuoteLineGrid';
import {
  QuotePlanningFields,
  type QuotePlanningDraft,
} from '../money/QuotePlanningFields';
import { useOnline } from '../ui/useConnection';
import { SearchInput } from '../ui/SearchInput';
import { useServerTable } from '../ui/useServerTable';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconQuotes } from '../icons';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from '../jobs/CommercialWriteRecovery';

export function NewQuoteSheet({
  open,
  onClose,
  commercialJobId,
}: {
  open: boolean;
  onClose: () => void;
  commercialJobId?: string;
}) {
  const trpc = useTRPC();
  const workflow = useQuery({
    ...trpc.jobPipeline.list.queryOptions(),
    enabled: open && !commercialJobId,
  });
  const workspaceNotice = (
    <StaleNotice
      show={
        !commercialJobId && (refreshFailed(workflow, workflow.data) || workflow.isPaused)
      }
      onRetry={() => void workflow.refetch()}
    />
  );
  if (commercialJobId || workflow.data?.enabled)
    return (
      <CommercialNewQuoteSheet
        key={commercialJobId ?? 'choose'}
        open={open}
        onClose={onClose}
        commercialJobId={commercialJobId}
        workspaceNotice={workspaceNotice}
      />
    );
  if (!open) return null;
  const state = shownState(workflow, workflow.data);
  if (state !== 'ready')
    return (
      <Sheet open={open} onClose={onClose} title="New quote">
        <UnresolvedState
          state={state}
          icon={IconQuotes}
          what="the quote workspace"
          error={workflow.error}
          onRetry={() => void workflow.refetch()}
        />
      </Sheet>
    );
  return (
    <LegacyNewQuoteSheet
      open={open}
      onClose={onClose}
      workspaceNotice={workspaceNotice}
    />
  );
}

function CommercialNewQuoteSheet({
  open,
  onClose,
  commercialJobId,
  workspaceNotice,
}: {
  open: boolean;
  onClose: () => void;
  commercialJobId?: string;
  workspaceNotice?: ReactNode;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const online = useOnline();
  const [selectedId, setSelectedId] = useState(commercialJobId ?? '');
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [title, setTitle] = useState('');
  const [taxPct, setTaxPct] = useState('');
  const [terms, setTerms] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [planning, setPlanning] = useState<QuotePlanningDraft>({});
  const recovery = useCommercialWriteRecovery('quote.create');
  const submitted = recovery.read.status === 'ready' ? recovery.read.request : null;
  useEffect(() => {
    if (!submitted) return;
    setSelectedId(submitted.commercialJobId ?? '');
    setWho({ customerId: submitted.customerId, propertyId: submitted.propertyId ?? '' });
    setTitle(submitted.title ?? '');
    setTaxPct(
      submitted.taxRateBps === undefined ? '' : String(submitted.taxRateBps / 100),
    );
    setTerms(submitted.termsText ?? '');
    setValidUntil(submitted.validUntil?.toISOString().slice(0, 10) ?? '');
    setPlanning({
      estimatorUserId: submitted.estimatorUserId ?? undefined,
      requestedStartAt: submitted.requestedStartAt?.toISOString(),
      requestedEndAt: submitted.requestedEndAt?.toISOString(),
    });
    setLines(
      submitted.lines.map((line) => ({
        ...line,
        requestedStartAt: line.requestedStartAt?.toISOString(),
        requestedEndAt: line.requestedEndAt?.toISOString(),
        qty: String(line.qty),
        price: (line.unitPriceCents / 100).toFixed(2),
      })),
    );
  }, [submitted]);
  const { search, setSearch, query, table } = useServerTable(10);
  const jobs = useQuery({
    ...trpc.commercialJob.list.queryOptions(query),
    enabled: open && !selectedId,
  });
  const jobsState = shownState(jobs, jobs.data);
  const job = useQuery({
    ...trpc.commercialJob.get.queryOptions({ commercialJobId: selectedId }),
    enabled: Boolean(selectedId),
  });
  const customerId = job.data?.job.customerId ?? who.customerId;
  const customer = useQuery({
    ...trpc.customer.get.queryOptions({ customerId }),
    enabled: Boolean(customerId),
  });
  const create = useMutation(
    trpc.quote.create.mutationOptions({
      onSuccess: async (quote, request) => {
        if (!(await recovery.complete(request))) return;
        void qc.invalidateQueries();
        onClose();
        void navigate({ to: '/quotes/$quoteId', params: { quoteId: quote.id } });
      },
    }),
  );
  const locked =
    create.isPending || recovery.preparing || recovery.read.status !== 'empty';
  const parsed = parseQuoteLines(lines);
  const complete = Boolean(
    selectedId &&
      job.data &&
      customerId &&
      title.trim() &&
      lines.length > 0 &&
      parsed.length === lines.length,
  );
  const save = async () => {
    if (!online || create.isPending || recovery.preparing || (!submitted && !complete))
      return;
    const request: QuoteCreateInput = submitted ?? {
      commercialJobId: selectedId,
      customerId,
      propertyId: job.data?.job.propertyId ?? (who.propertyId || undefined),
      title: title.trim(),
      termsText: terms || undefined,
      validUntil: validUntil ? new Date(`${validUntil}T12:00:00`) : undefined,
      taxRateBps: taxBps(taxPct),
      estimatorUserId: planning.estimatorUserId,
      requestedStartAt: planning.requestedStartAt
        ? new Date(planning.requestedStartAt)
        : undefined,
      requestedEndAt: planning.requestedEndAt
        ? new Date(planning.requestedEndAt)
        : undefined,
      lines: parsed,
      ...idem(),
    };
    if (await recovery.prepare(request)) create.mutate(request);
  };
  return (
    <Sheet
      wide
      open={open}
      onClose={() => {
        if (!create.isPending && !recovery.preparing) onClose();
      }}
      title="New quote"
      footer={
        <div className="space-y-3">
          <CommercialWriteRecovery
            read={recovery.read}
            error={recovery.error}
            pending={create.isPending || recovery.preparing}
            online={online}
            onRetry={() => void save()}
            onResolve={() => void recovery.resolveAttempt()}
          />
          {recovery.read.status === 'empty' ? (
            <Button
              className="w-full"
              disabled={!online || create.isPending || recovery.preparing || !complete}
              onClick={() => void save()}
            >
              {create.isPending ? 'Creating…' : 'Create draft quote'}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        {workspaceNotice}
        {!online ? (
          <Notice tone="warn">
            Reconnect to create this quote. Your current entries stay here.
          </Notice>
        ) : null}
        {create.error ? (
          <Notice tone="danger" alert>
            {create.error.message}
          </Notice>
        ) : null}
        {!selectedId ? (
          <section className="space-y-3" aria-label="Choose the parent job">
            <StaleNotice
              show={refreshFailed(jobs, jobs.data) || jobs.isPaused}
              onRetry={() => void jobs.refetch()}
            />
            <p className="text-sm">Choose the Job this quote belongs to.</p>
            <SearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search jobs"
            />
            {jobsState !== 'ready' ? (
              <UnresolvedState
                state={jobsState}
                icon={IconQuotes}
                what="jobs"
                error={jobs.error}
                onRetry={() => void jobs.refetch()}
              />
            ) : jobs.data ? (
              <>
                <ul className="divide-y divide-border">
                  {jobs.data.rows.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="w-full rounded-[var(--radius-sm)] py-3 text-left hover:bg-surface-2"
                        onClick={() => {
                          setSelectedId(row.id);
                          setTitle(row.title);
                        }}
                      >
                        <span className="block text-sm font-semibold">{row.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatCommercialJobNumber(row.jobNumber)} ·{' '}
                          {row.customerName || 'Customer not assigned'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {jobs.data.rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No matching jobs.{' '}
                    <Link to="/jobs" className="font-semibold underline">
                      Create a Job first
                    </Link>
                    .
                  </p>
                ) : null}
                <div className="flex items-center justify-between gap-2 text-xs">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={query.offset === 0 || jobs.isFetching}
                    onClick={() =>
                      table(jobs.data.total).onPage(query.offset / query.limit - 1)
                    }
                  >
                    Previous
                  </Button>
                  <span>{jobs.data.total} jobs</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={
                      query.offset + query.limit >= jobs.data.total || jobs.isFetching
                    }
                    onClick={() =>
                      table(jobs.data.total).onPage(query.offset / query.limit + 1)
                    }
                  >
                    Next
                  </Button>
                </div>
              </>
            ) : null}
          </section>
        ) : (
          <>
            <StaleNotice
              show={refreshFailed(job, job.data) || job.isPaused}
              onRetry={() => void job.refetch()}
            />
            {!job.data ? (
              <p className="text-sm text-muted-foreground">
                {job.isError
                  ? 'The selected Job could not load. Retry before creating a quote.'
                  : 'Loading Job…'}
              </p>
            ) : (
              <>
                <div className="rounded-[var(--radius)] bg-surface-2 p-3 text-sm">
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: selectedId }}
                    className="font-semibold hover:underline"
                  >
                    {formatCommercialJobNumber(job.data.job.jobNumber)} ·{' '}
                    {job.data.job.title}
                  </Link>
                  <p className="mt-1 text-muted-foreground">
                    {job.data.job.customerName || 'Choose the customer below'}
                    {job.data.job.propertyLabel ? ` · ${job.data.job.propertyLabel}` : ''}
                  </p>
                  {!commercialJobId && !locked ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => setSelectedId('')}
                    >
                      Choose another Job
                    </Button>
                  ) : null}
                </div>
                <fieldset disabled={locked} className="space-y-4">
                  {!job.data.job.customerId ? (
                    <CustomerPicker
                      customerId={who.customerId}
                      propertyId={who.propertyId}
                      onChange={setWho}
                      requireProperty={false}
                    />
                  ) : null}
                  {job.data.job.customerId && !job.data.job.propertyId ? (
                    <Field label="Service address">
                      <select
                        className={field}
                        value={who.propertyId}
                        onChange={(event) =>
                          setWho({ customerId, propertyId: event.target.value })
                        }
                      >
                        <option value="">Choose an address before acceptance</option>
                        {(customer.data?.properties ?? []).map((property) => (
                          <option key={property.id} value={property.id}>
                            {property.label}
                            {property.address1 ? ` · ${property.address1}` : ''}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                  <TemplatePicker
                    appliesTo="quote"
                    onPick={(fill) => {
                      setTitle(fill.title);
                      setTaxPct(fill.taxPct);
                      setLines(fill.lines.length ? fill.lines : [emptyLine()]);
                    }}
                  />
                  <Field label="Quote title">
                    <input
                      className={field}
                      value={title}
                      maxLength={200}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </Field>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TaxRateField
                      value={taxPct}
                      onChange={setTaxPct}
                      exempt={customer.data?.customer.taxExempt}
                    />
                    <Field label="Valid until">
                      <input
                        className={field}
                        type="date"
                        value={validUntil}
                        onChange={(event) => setValidUntil(event.target.value)}
                      />
                    </Field>
                  </div>
                  <QuotePlanningFields
                    value={planning}
                    onChange={setPlanning}
                    disabled={locked}
                  />
                  <QuoteLineGrid lines={lines} onChange={setLines} disabled={locked} />
                  <Field label="Terms">
                    <textarea
                      className={`${field} min-h-20 py-2`}
                      value={terms}
                      maxLength={4000}
                      onChange={(event) => setTerms(event.target.value)}
                    />
                  </Field>
                </fieldset>
                <QuoteTotalsPreview
                  customerId={customerId || undefined}
                  taxRateBps={taxBps(taxPct)}
                  lines={parsed}
                />
                <p className="text-xs text-muted-foreground">
                  This creates a separate quote within the Job. Revisions of an existing
                  quote stay with that original quote.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

/** Write a quote from scratch — the phone-call path, with no request to triage. */
function LegacyNewQuoteSheet({
  open,
  onClose,
  workspaceNotice,
}: {
  open: boolean;
  onClose: () => void;
  workspaceNotice?: ReactNode;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [title, setTitle] = useState('');
  const [taxPct, setTaxPct] = useState('');

  // One customer's exemption, asked for by id. This used to read `.find()` over the
  // whole customer list — 500 rows downloaded for one boolean, and past the cap the
  // fallback `?? false` silently TAXED a tax-exempt customer. CustomerPicker below
  // already fetches this same query, so react-query serves it from cache.
  const chosen = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: who.customerId }),
    enabled: Boolean(who.customerId),
  });
  const exempt = chosen.data?.customer.taxExempt ?? false;
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);

  const create = useMutation(
    trpc.quote.create.mutationOptions({
      onSuccess: (quote) => {
        void qc.invalidateQueries();
        setWho({ customerId: '', propertyId: '' });
        setTitle('');
        setTaxPct('');
        setLines([emptyLine()]);
        onClose();
        void navigate({ to: '/quotes/$quoteId', params: { quoteId: quote.id } });
      },
    }),
  );

  const parsed = parseLines(lines);
  const valid = who.customerId && title.trim().length > 0 && parsed.length > 0;

  return (
    <Sheet
      // Sonar-format width: a line-item builder is cramped at 420px (104).
      wide
      open={open}
      onClose={onClose}
      title="New quote"
      footer={
        <Button
          className="w-full"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({
              customerId: who.customerId,
              propertyId: who.propertyId || undefined,
              title: title.trim(),
              // Tax is entered as a percentage and stored in basis points.
              // Blank now means "use the shop's rate" rather than 0% — the same rule
              // every other money screen follows.
              taxRateBps: taxBps(taxPct),
              lines: parsed,
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Creating…' : 'Create quote'}
        </Button>
      }
    >
      <div className="space-y-3">
        {workspaceNotice}
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <CustomerPicker
          customerId={who.customerId}
          propertyId={who.propertyId}
          onChange={setWho}
          requireProperty={false}
        />

        <TemplatePicker
          appliesTo="quote"
          onPick={(fill) => {
            setTitle(fill.title);
            setTaxPct(fill.taxPct);
            setLines(fill.lines.length ? fill.lines : [emptyLine()]);
          }}
        />

        <Field label="What's the job?">
          <input
            className={field}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Replace 40-gal water heater"
          />
        </Field>

        <TaxRateField value={taxPct} onChange={setTaxPct} exempt={exempt} />

        <LineItemBuilder
          lines={lines}
          onChange={setLines}
          label="What are you quoting?"
        />

        <p className="text-xs text-muted-foreground">
          It starts as a draft — you decide when to send it.
        </p>
      </div>
    </Sheet>
  );
}
